import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ControlBarButton } from '@m12s/component-library';
import { user, random } from 'lib/icons';
import { UtilityMenu } from 'components/UtilityMenu';
import useSelectOperator from 'lib/hooks/useSelectOperator';
import TokenStore from 'lib/api/links/TokenStore';
import { getMachine } from 'lib/selectors/getMachine';
import { getCompany } from 'lib/selectors/getCompany';
import { getHasOpenJob } from 'lib/selectors/getHasOpenJob';
import Routes from 'lib/utils/routes';
import {
  ControlBarResponsive,
  MobileIcon,
  ResponsiveText,
  Scrollable,
  SingleButtonScrollable,
} from '../styled';

/**
 * Control bar component for signed-out state in non-ERP mode.
 *
 * This component is rendered when:
 * - No operator is signed in
 * - System is NOT in ERP-driven mode
 * - No job is currently running
 *
 * In this state, the user must sign in before they can start working.
 * If there's no open job, signing in will redirect to job selection.
 * When machine switching is enabled for the company, a "Change machine" button
 * is also shown here (mirroring the hamburger menu action) so operators can
 * switch machines without signing in first — matching the demo.
 *
 * @returns {JSX.Element} Control bar with sign-in (and optionally change-machine)
 */
const SignedOut = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const company = useSelector(getCompany);
  const hasOpenJob = useSelector(getHasOpenJob);
  const { openSelectOperator } = useSelectOperator();

  const machineSwitchingEnabled = !!company?.machineSwitching;
  // Two buttons need the flex row; a lone sign-in button stays centered.
  const Container = machineSwitchingEnabled
    ? Scrollable
    : SingleButtonScrollable;

  return (
    <ControlBarResponsive id="job-control-bar">
      <UtilityMenu />
      <Container>
        <ControlBarButton
          id="job-control-bar-sign-in"
          onClick={() => {
            // If no job is open, redirect to job selection after sign-in
            if (!hasOpenJob) {
              return openSelectOperator({
                redirectPath: Routes.machineIdSelectJobPath(machine.id),
              });
            }

            // Otherwise, just open operator selection
            return openSelectOperator();
          }}
        >
          <MobileIcon icon={user} />
          <ResponsiveText
            full={t('signIn|full')}
            medium={t('signIn|medium')}
            short={t('signIn|short')}
          />
        </ControlBarButton>

        {machineSwitchingEnabled && (
          <ControlBarButton
            id="job-control-bar-change-machine"
            onClick={() => {
              // Mirror the hamburger's switch-machine action: clear any preset
              // machine token, then route to machine selection.
              new TokenStore('presetMachine').clear();
              return history.push(Routes.switchMachinesPath());
            }}
          >
            <MobileIcon icon={random} />
            <ResponsiveText
              full={t('changeMachine|full')}
              medium={t('changeMachine|medium')}
              short={t('changeMachine|short')}
            />
          </ControlBarButton>
        )}
      </Container>
    </ControlBarResponsive>
  );
};

export { SignedOut };
